<!-- ── Dialog wrapper ─────────────────────────────────────────────────────── -->
<div class="import-dialog">
  <!-- ── Header ────────────────────────────────────────────────────────────── -->
  <div class="import-dialog__header">
    <h2 class="import-dialog__title">{{ titleKey | translate }}</h2>
    <button class="import-dialog__close" type="button" (click)="close()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- ── Step indicator ────────────────────────────────────────────────────── -->
  <div class="import-dialog__steps">
    <span
      class="import-dialog__step"
      [class.import-dialog__step--active]="currentStep === 'upload'"
      [class.import-dialog__step--done]="currentStep === 'preview'"
    >
      <span class="import-dialog__step-circle">
        <mat-icon *ngIf="currentStep === 'preview'" class="import-dialog__step-check">check</mat-icon>
      </span>
      {{ "knowledge_base.import.step_upload" | translate }}
    </span>
    <span class="import-dialog__step-arrow">›</span>
    <span class="import-dialog__step" [class.import-dialog__step--active]="currentStep === 'preview'">
      <span class="import-dialog__step-circle"></span>
      {{ "knowledge_base.import.step_preview" | translate }}
    </span>
  </div>

  <!-- ── Info line ──────────────────────────────────────────────────────────── -->
  <div class="import-dialog__info-row">
    <mat-icon class="import-dialog__info-icon">info_outline</mat-icon>
    <p class="import-dialog__info">{{ infoKey | translate }}</p>
  </div>

  <!-- ══ UPLOAD STEP ══════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 'upload'">
    <!-- Template download buttons -->
    <div class="import-dialog__template-row">
      <span class="import-dialog__template-label">{{ "knowledge_base.import.download_template" | translate }}:</span>
      <button class="import-dialog__template-btn" type="button" (click)="downloadTemplate('csv')">
        <mat-icon class="import-dialog__template-icon">file_download</mat-icon>
        .csv
      </button>
      <button class="import-dialog__template-btn" type="button" (click)="downloadTemplate('xlsx')">
        <mat-icon class="import-dialog__template-icon">file_download</mat-icon>
        .xlsx
      </button>
    </div>

    <!-- Drop zone -->
    <div
      class="import-dialog__dropzone"
      [class.import-dialog__dropzone--dragover]="isDragOver"
      [class.import-dialog__dropzone--has-file]="!!selectedFile"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)"
      (click)="fileInput.click()"
    >
      <input #fileInput type="file" [accept]="acceptAttr" class="import-dialog__file-input" (change)="onFileInputChange($event)" />

      <ng-container *ngIf="!selectedFile">
        <mat-icon class="import-dialog__dropzone-icon">cloud_upload</mat-icon>
        <p class="import-dialog__dropzone-text">{{ "knowledge_base.import.drag_drop" | translate }}</p>
        <p class="import-dialog__dropzone-hint">
          {{ "knowledge_base.import.drag_drop_hint" | translate }}
        </p>
      </ng-container>

      <ng-container *ngIf="selectedFile">
        <mat-icon class="import-dialog__dropzone-icon import-dialog__dropzone-icon--success">check_circle</mat-icon>
        <p class="import-dialog__dropzone-text">{{ "knowledge_base.import.file_selected" | translate }}: {{ selectedFile.name }}</p>
      </ng-container>
    </div>

    <!-- File error -->
    <p *ngIf="fileError" class="import-dialog__file-error">{{ fileError }}</p>
  </ng-container>
  <!-- ══ END UPLOAD STEP ══════════════════════════════════════════════════════ -->

  <!-- ══ PREVIEW STEP ═════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="currentStep === 'preview' && previewData">
    <!-- Overwrite warning (preferred products: existing pairs are replaced) -->
    <div *ngIf="isPreferredProductType" class="import-dialog__overwrite-warning">
      <mat-icon class="import-dialog__overwrite-warning-icon">warning_amber</mat-icon>
      <span>{{ "knowledge_base.import.preferred_products.overwrite_warning" | translate }}</span>
    </div>

    <!-- Summary cards -->
    <div class="import-dialog__summary">
      <div class="import-dialog__summary-card">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.total_rows" | translate }}</span>
        <span class="import-dialog__summary-value">{{ totalRows }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--ready" *ngIf="validCount > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.valid" | translate }}</span>
        <span class="import-dialog__summary-value">{{ validCount }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--warning" *ngIf="warningCount > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.warnings" | translate }}</span>
        <span class="import-dialog__summary-subtitle">{{ "knowledge_base.import.summary.warnings_subtitle" | translate }}</span>
        <span class="import-dialog__summary-value">{{ warningCount }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--duplicates" *ngIf="duplicates > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.duplicates" | translate }}</span>
        <span class="import-dialog__summary-subtitle">{{ "knowledge_base.import.summary.duplicates_subtitle" | translate }}</span>
        <span class="import-dialog__summary-value">{{ duplicates }}</span>
      </div>
      <div class="import-dialog__summary-card import-dialog__summary-card--error" *ngIf="errors > 0">
        <span class="import-dialog__summary-label">{{ "knowledge_base.import.summary.errors" | translate }}</span>
        <span class="import-dialog__summary-value">{{ errors }}</span>
      </div>
    </div>

    <!-- Row table -->
    <div class="import-dialog__table-wrapper">
      <table class="import-dialog__table">
        <thead>
          <tr>
            <th *ngIf="!isPreferredProductType">
              {{ "knowledge_base.import.col.sku" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="isAliasType">
              {{ "knowledge_base.import.col.alias" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="!isAliasType">
              {{ "knowledge_base.import.col.customer" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="!isAliasType && !isPreferredProductType">
              {{ "knowledge_base.import.col.external_reference" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th *ngIf="isPreferredProductType">
              {{ "knowledge_base.import.col.product" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th>
              {{ "knowledge_base.import.col.status" | translate }}
              <mat-icon class="import-dialog__sort-icon">unfold_more</mat-icon>
            </th>
            <th>
              {{ "knowledge_base.import.col.reason" | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of previewData.rows">
            <td *ngIf="!isPreferredProductType">{{ getDisplaySku(row) }}</td>
            <td *ngIf="isAliasType">{{ getDisplayAlias(row) }}</td>
            <td *ngIf="!isAliasType">{{ getDisplayCustomer(row) }}</td>
            <td *ngIf="!isAliasType && !isPreferredProductType">{{ getDisplayReferenceCode(row) }}</td>
            <td *ngIf="isPreferredProductType">{{ getDisplayProduct(row) }}</td>
            <td>
              <span class="import-dialog__badge import-dialog__badge--{{ getBadgeType(row) }}">
                {{ getBadgeLabel(row) }}
              </span>
            </td>
            <td class="import-dialog__reason-cell">{{ getRowReason(row) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>
  <!-- ══ END PREVIEW STEP ══════════════════════════════════════════════════════ -->

  <!-- ── Loading overlay (dry-run in progress) ──────────────────────────────── -->
  <div *ngIf="isLoadingPreview" class="import-dialog__loading">
    <mat-spinner diameter="32"></mat-spinner>
  </div>

  <!-- ── Footer actions ────────────────────────────────────────────────────── -->
  <div class="import-dialog__footer">
    <button class="import-dialog__cancel-btn" type="button" (click)="close()">
      {{ "knowledge_base.import.cancel" | translate }}
    </button>

    <!-- Upload step: PROCEED -->
    <ui-button
      *ngIf="currentStep === 'upload'"
      type="primary"
      [label]="'knowledge_base.import.proceed' | translate"
      [disabled]="!selectedFile || isLoadingPreview"
      (click)="proceed()"
    >
    </ui-button>

    <!-- Preview step: CONFIRM -->
    <ui-button
      *ngIf="currentStep === 'preview'"
      type="primary"
      [label]="'knowledge_base.import.confirm' | translate"
      [disabled]="isConfirming"
      (click)="confirmImport()"
    >
    </ui-button>
  </div>
</div>
